import React, { useState, useRef, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { abortUpload, UploadProgress, UploadTask } from '@Utils/S3Storage'
import map from 'lodash/map'
import find from 'lodash/find'
import filter from 'lodash/filter'
import some from 'lodash/some'
import getFileDuration from '@Utils/GetFileDuration'
import { getRemoteMediaDuration, rememberRemoteMediaDuration } from '@Utils/GetRemoteMediaDuration'
import UploadArea from '@SiteComponents/UploadArea'
import TrainingSampleUploader, { ITrainingSampleUploaderOptions, IUploadedTrainingSample } from '@Utils/TrainingSampleUploader'
import S3Remover from '@Utils/S3Remover'
import getTimecode from '@Utils/GetTimecode'
import Button from '@SiteComponents/Button'
import Mic from '@Images/start_recording_audio.svg'
import EditSpeaker from '@Images/edit_speaker.svg'

import TrainingSample from './TrainingSample'

import {
  IDraftSpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import './TrainingSamples.scss'

export interface IFileToUpload {
  readonly fileId: string
  readonly file: File
  readonly hasStartedUpload: boolean
  readonly hasCompletedUpload: boolean
  readonly uploadProgress: number
  readonly duration?: number
  readonly uploadingRequest?: UploadTask
  readonly url?: string
}

interface TrainingSamplesProps {
  readonly draftSpeakerVoice: IDraftSpeakerVoice
  readonly originalSpeakerVoiceState: IDraftSpeakerVoice
  readonly filesToUpload: IFileToUpload[]
  readonly onOpenTextDescriptionMode?: () => void
  setFilesToUpload: React.Dispatch<React.SetStateAction<IFileToUpload[]>>
  handleDraftChange(updatedFields: Partial<IDraftSpeakerVoice>): void
}

const MAX_FILE_SIZE = 10486260 // Bytes = 10MB
const MAX_FILE_SIZE_IN_MB = MAX_FILE_SIZE / 1048576
const MAX_FILE_DURATION = 120 // Seconds
const MAX_NUMBER_OF_TRAINING_SAMPLES = 10
const MAX_NUMBER_OF_SIMULTANEOUS_UPLOADS = 2
const ACCEPTED_AUDIO_EXTENSIONS = ['wav', 'flac', 'ogg', 'mp3']
const ACCEPT_ATTRIBUTE = ACCEPTED_AUDIO_EXTENSIONS.map(extension => `.${extension}`).join(',')
// mp3 is not a format the voice pipeline consumes directly, so it is decoded and
// re-encoded to WAV in the browser before upload (same path the recorder uses).
const EXTENSIONS_REQUIRING_WAV_CONVERSION = ['mp3']

const getAudioContext = () => {
  const AudioContextConstructor = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  return AudioContextConstructor ? new AudioContextConstructor() : undefined
}

const encodeAudioBufferToWav = (audioBuffer: AudioBuffer): Blob => {
  const { length, numberOfChannels, sampleRate } = audioBuffer
  const bytesPerSample = 2
  const blockAlign = numberOfChannels * bytesPerSample
  const buffer = new ArrayBuffer(44 + length * blockAlign)
  const view = new DataView(buffer)

  const writeString = (stringOffset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(stringOffset + index, value.charCodeAt(index))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + length * blockAlign, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, 16, true)
  writeString(36, 'data')
  view.setUint32(40, length * blockAlign, true)

  const channels = Array.from({ length: numberOfChannels }, (_value, index) => audioBuffer.getChannelData(index))
  let offset = 44

  for (let sampleIndex = 0; sampleIndex < length; sampleIndex += 1) {
    channels.forEach(channel => {
      const sample = Math.max(-1, Math.min(1, channel[sampleIndex]))
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true)
      offset += bytesPerSample
    })
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

const convertBlobToWav = async (blob: Blob): Promise<Blob> => {
  const audioContext = getAudioContext()

  if (!audioContext) {
    throw new Error('Audio conversion is not supported in this browser.')
  }

  try {
    const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer())
    return encodeAudioBufferToWav(audioBuffer)
  } finally {
    audioContext.close().catch(() => undefined)
  }
}

const isAcceptedAudioFormat = async (file: File): Promise<boolean> => {
  const buffer = await file.slice(0, 12).arrayBuffer()
  const bytes = new Uint8Array(buffer)

  // WAV: 'RIFF' at offset 0 and 'WAVE' at offset 8
  const isWav = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45

  // FLAC: 'fLaC' at offset 0
  const isFlac = bytes[0] === 0x66 && bytes[1] === 0x4C && bytes[2] === 0x61 && bytes[3] === 0x43

  // OGG: 'OggS' at offset 0
  const isOgg = bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53

  // MP3: either an ID3v2 tag ('ID3') or a raw MPEG frame sync (11 bits set)
  const hasId3Tag = bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33
  // Frame sync = 11 bits set: 0xFF then the top 3 bits of the next byte, which for
  // a single byte is equivalent to >= 0xE0 (avoids a bitwise op the linter rejects).
  const hasMpegFrameSync = bytes[0] === 0xFF && bytes[1] >= 0xE0
  const isMp3 = hasId3Tag || hasMpegFrameSync

  return isWav || isFlac || isOgg || isMp3
}

const requiresWavConversion = (file: File): boolean => {
  return EXTENSIONS_REQUIRING_WAV_CONVERSION.some(extension => file.name.toLowerCase().endsWith(`.${extension}`))
}

const convertFileToWav = async (file: File): Promise<File> => {
  const wavBlob = await convertBlobToWav(file)
  const wavName = `${file.name.replace(/\.[^.]+$/, '')}.wav`

  return new File([wavBlob], wavName, { type: 'audio/wav' })
}

const TrainingSamples: React.FC<TrainingSamplesProps> = props => {
  const {
    draftSpeakerVoice,
    originalSpeakerVoiceState,
    filesToUpload,
  } = props

  const {
    id,
    trainingUrls: trainingUrls = [],
  } = draftSpeakerVoice

  const {
    trainingUrls: originalTrainingUrls = [],
  } = originalSpeakerVoiceState

  const fileRef = useRef<HTMLInputElement>(null)
  const filesToUploadRef = useRef<IFileToUpload[]>(filesToUpload)
  filesToUploadRef.current = filesToUpload

  const trainingUrlsRef = useRef<string[]>(trainingUrls)
  trainingUrlsRef.current = trainingUrls

  const [limitsUploadAlert, setLimitsUploadAlert] = useState<string | null>(null)
  const [existingDurations, setExistingDurations] = useState<Record<string, number | undefined>>({})
  const [isPreparingRecording, setIsPreparingRecording] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingError, setRecordingError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const recordingStreamRef = useRef<MediaStream | null>(null)
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const uploadCounter = filter(filesToUpload, fileToUpload => fileToUpload.hasStartedUpload && !fileToUpload.hasCompletedUpload).length
  const showLimitsUploadAlert = !!limitsUploadAlert
  const uploadingFiles = filter(filesToUpload, file => !file.hasCompletedUpload)
  const isMediaRecorderSupported = typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined'

  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop()
      }

      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach(track => track.stop())
        recordingStreamRef.current = null
      }

      mediaRecorderRef.current = null
    }
  }, [])

  useEffect(() => {
    if (trainingUrls.length === 0) {
      setExistingDurations({})
      return
    }

    const urls = Array.from(new Set(trainingUrls))
    let cancelled = false

    const loadDurations = async () => {
      const entries = await Promise.all(
        urls.map(async url => {
          const duration = await getRemoteMediaDuration(url)
          return [url, duration] as const
        })
      )

      if (cancelled) { return }

      const fetched = entries.reduce<Record<string, number | undefined>>((acc, [url, duration]) => {
        if (duration !== undefined) {
          acc[url] = duration
        }
        return acc
      }, {})

      setExistingDurations(prev => {
        const next: Record<string, number | undefined> = {}

        urls.forEach(url => {
          next[url] = fetched[url] ?? prev[url]
        })

        return next
      })
    }

    loadDurations()

    return () => {
      cancelled = true
    }
  }, [trainingUrls])

  useEffect(() => {
    if (uploadCounter >= MAX_NUMBER_OF_SIMULTANEOUS_UPLOADS) { return }

    const nextFileToUpload = find(filesToUpload, file => !file.hasStartedUpload)

    if (!nextFileToUpload) { return }

    updateHasStartedUpload(nextFileToUpload.fileId)

    uploadFile(nextFileToUpload.file as File, nextFileToUpload.fileId)
  }, [filesToUpload, trainingUrls, uploadCounter])

  const updateUploadProgress = (fileId: string, uploadProgress: number) => {
    props.setFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.fileId === fileId
          ? { ...file, uploadProgress }
          : file
      )
    )
  }

  const updateHasStartedUpload = (fileId: string) => {
    props.setFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.fileId === fileId
          ? { ...file, hasStartedUpload: true }
          : file
      )
    )
  }

  const updateHasCompletedUpload = (fileId: string, url: string) => {
    props.setFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.fileId === fileId
          ? { ...file, hasCompletedUpload: true, url }
          : file
      )
    )
  }

  const updateUploadingRequest = (fileId: string, uploadingRequest: UploadTask) => {
    props.setFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.fileId === fileId
          ? { ...file, uploadingRequest }
          : file
      )
    )
  }

  const cancelUpload = async (fileId: string) => {
    const fileEntry = props.filesToUpload.find(file => file.fileId === fileId)

    if (fileEntry?.uploadingRequest) {
      const uploadAborted = await abortUpload(fileEntry.uploadingRequest)
      if (!uploadAborted) { return }
    }

    props.setFilesToUpload(prevFiles => prevFiles.filter(file => file.fileId !== fileId))
  }

  const openFileManager = () => {
    if (!fileRef) { return }
    if (!fileRef.current) { return }

    fileRef.current.click()
  }

  const handleUploadClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files
    if (!files) { return }

    handleUpload(Array.from(files))

    e.target.value = '' // Set to an empty string to allow the upload of the same file again after aborting
  }

  const uploadFile = (file: File, fileId: string) => {
    const options: ITrainingSampleUploaderOptions = {
      uploadCallback: (params: IUploadedTrainingSample) => {
        updateUploadProgress(fileId, 100)
        updateHasCompletedUpload(fileId, params.url)

        const durationForFile = filesToUploadRef.current.find(fileToUpload => fileToUpload.fileId === fileId)?.duration

        if (durationForFile !== undefined) {
          rememberRemoteMediaDuration(params.url, durationForFile)
          setExistingDurations(prev => ({ ...prev, [params.url]: durationForFile }))
        }

        props.handleDraftChange({ trainingUrls: [params.url, ...trainingUrlsRef.current] })
      },
      showProgressCallback: (data: UploadProgress) => {
        const uploadProgress = (data.loaded ?? 0) / file.size * 100
        updateUploadProgress(fileId, uploadProgress)
      },
      speakerVoiceId: id
    }

    const uploadingRequest = new TrainingSampleUploader(file, options).call()
    updateUploadingRequest(fileId, uploadingRequest)
  }

  const handleUpload = async (files: File[]) => {
    setLimitsUploadAlert(null)

    if (some(files, file => !file.type.startsWith('audio/') || !ACCEPTED_AUDIO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(`.${ext}`)))) {
      setLimitsUploadAlert(`Only ${ACCEPTED_AUDIO_EXTENSIONS.join(', ')} files are supported.`)
      return
    }

    const magicBytesResults = await Promise.all(files.map(isAcceptedAudioFormat))

    if (some(magicBytesResults, result => !result)) {
      setLimitsUploadAlert(`One or more files are not valid ${ACCEPTED_AUDIO_EXTENSIONS.join(', ')} files.`)
      return
    }

    if (files.length + uploadingFiles.length + trainingUrls.length > MAX_NUMBER_OF_TRAINING_SAMPLES) {
      setLimitsUploadAlert(`You can upload a maximum of ${MAX_NUMBER_OF_TRAINING_SAMPLES} files.`)
      return
    }

    if (some(files, file => file.size > MAX_FILE_SIZE)) {
      setLimitsUploadAlert(`Some files exceed the ${MAX_FILE_SIZE_IN_MB} MB size limit.`)
      return
    }

    const filesWithDuration = await Promise.all(
      files.map(async file => {
        const duration = await getFileDuration(file) // silently returns undefined if it fails
        return { file, duration }
      })
    )

    const exceedingDuration = filesWithDuration.filter(({ duration }) => {
      return duration !== undefined && duration > MAX_FILE_DURATION
    })

    if (exceedingDuration.length > 0) {
      setLimitsUploadAlert(`Some files exceed the ${Math.round(MAX_FILE_DURATION / 60)} minute duration limit.`)
      return
    }

    // Formats the pipeline cannot consume directly (mp3) are decoded and re-encoded
    // to WAV here, so everything that reaches S3 is already in the expected format.
    let filesReadyForUpload: typeof filesWithDuration

    try {
      filesReadyForUpload = await Promise.all(
        filesWithDuration.map(async ({ file, duration }) => ({
          file: requiresWavConversion(file) ? await convertFileToWav(file) : file,
          duration,
        }))
      )
    } catch {
      setLimitsUploadAlert('Some files could not be converted to a supported format. Please try another file.')
      return
    }

    const newFilesToUpload = filesReadyForUpload.map(({ file, duration }) => ({
      fileId: uuidv4(),
      file,
      hasStartedUpload: false,
      uploadProgress: 0,
      hasCompletedUpload: false,
      duration
    }))

    props.setFilesToUpload(prev => [...prev, ...newFilesToUpload])
  }

  const handleFileDrop = (filesList: FileList) => {
    const allFiles = Array.from(filesList)
    const files = allFiles.filter(item => item.type.startsWith('audio/') && ACCEPTED_AUDIO_EXTENSIONS.some(ext => item.name.toLowerCase().endsWith(`.${ext}`)))

    if (files.length < allFiles.length) {
      setLimitsUploadAlert(`Only ${ACCEPTED_AUDIO_EXTENSIONS.join(', ')} files are supported.`)
      return
    }

    if (files.length > 0) {
      handleUpload(files)
    }
  }

  const clearRecordingTimeout = () => {
    if (recordingTimeoutRef.current !== null) {
      clearTimeout(recordingTimeoutRef.current)
      recordingTimeoutRef.current = null
    }
  }

  const cleanupRecordingResources = () => {
    clearRecordingTimeout()
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(track => track.stop())
      recordingStreamRef.current = null
    }

    mediaRecorderRef.current = null
  }

  const startRecording = async () => {
    if (!isMediaRecorderSupported) {
      setRecordingError('Recording is not supported in this browser.')
      return
    }

    if (trainingUrls.length + uploadingFiles.length >= MAX_NUMBER_OF_TRAINING_SAMPLES) {
      setLimitsUploadAlert(`You can upload a maximum of ${MAX_NUMBER_OF_TRAINING_SAMPLES} files.`)
      return
    }

    setLimitsUploadAlert(null)
    setRecordingError(null)
    setIsPreparingRecording(true)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      recordingStreamRef.current = stream

      let recorderOptions: MediaRecorderOptions | undefined
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          recorderOptions = { mimeType: 'audio/webm;codecs=opus' }
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          recorderOptions = { mimeType: 'audio/webm' }
        }
      }

      const mediaRecorder = new MediaRecorder(stream, recorderOptions)
      mediaRecorderRef.current = mediaRecorder
      recordedChunksRef.current = []

      mediaRecorder.addEventListener('dataavailable', event => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data)
        }
      })

      mediaRecorder.addEventListener('stop', async () => {
        const mimeType = recorderOptions?.mimeType || 'audio/webm'
        const blob = new Blob(recordedChunksRef.current, { type: mimeType })

        cleanupRecordingResources()
        setIsRecording(false)
        setIsPreparingRecording(false)

        if (blob.size === 0) {
          setRecordingError('Unable to capture audio from microphone.')
          return
        }

        try {
          const wavBlob = await convertBlobToWav(blob)
          const fileName = `${new Date().toISOString()}.wav`
          const recordedFile = new File([wavBlob], fileName, { type: wavBlob.type })
          handleUpload([recordedFile])
        } catch (error) {
          setRecordingError(error instanceof Error ? error.message : 'Unable to prepare recorded audio.')
        }
      })

      mediaRecorder.addEventListener('error', event => {
        setRecordingError(event.error?.message ?? 'Recording failed.')
        setIsRecording(false)
        setIsPreparingRecording(false)
        cleanupRecordingResources()
      })

      mediaRecorder.start()
      setIsRecording(true)

      clearRecordingTimeout()
      recordingTimeoutRef.current = setTimeout(() => {
        setRecordingError(`Recording stopped after reaching the ${Math.round(MAX_FILE_DURATION / 60)} minute limit.`)
        stopRecording()
      }, (MAX_FILE_DURATION - 1) * 1000)
    } catch (error) {
      setRecordingError('Microphone access was denied.')
      setIsPreparingRecording(false)
      cleanupRecordingResources()
    } finally {
      setIsPreparingRecording(false)
    }
  }

  const stopRecording = () => {
    clearRecordingTimeout()
    const recorder = mediaRecorderRef.current

    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
  }

  const uploadingFilesComponents = map(uploadingFiles, file => {
    const uploadProgress = Math.round(file.uploadProgress)

    const onRemove = () => {
      void cancelUpload(file.fileId)
    }

    return (
      <TrainingSample
        key={file.fileId}
        fileName={file.file.name}
        progress={uploadProgress}
        onRemove={onRemove}
        allowRemoval={!file.hasCompletedUpload}
      />
    )
  })

  const existingFilesComponents = map(trainingUrls, url => {
    const fileName = url.split('/').pop()
    const durationValue = existingDurations[url]
    const durationTimeCode = durationValue !== undefined ? getTimecode(durationValue, false) : '—'

    const removeFromDraft = () => {
      const updatedUrls = filter(trainingUrls, existingUrl => existingUrl !== url)
      props.handleDraftChange({ trainingUrls: updatedUrls })
    }

    const onRemove = () => {
      if (!originalTrainingUrls.includes(url)) {
        const remover = new S3Remover({
          fileUrl: url,
        })
        remover.call().then(removeFromDraft).catch(() => undefined)
      } else {
        removeFromDraft()
      }
    }

    return (
      <TrainingSample
        key={url}
        fileName={fileName || 'Unknown'}
        url={url}
        duration={durationTimeCode}
        onRemove={onRemove}
        allowRemoval
      />
    )
  })

  const handleRecordClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const handleOpenTextDescriptionModeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    props.onOpenTextDescriptionMode?.()
  }

  const renderRecordButton = () => {
    if (!isMediaRecorderSupported) {
      return null
    }

    const isButtonDisabled = isPreparingRecording || (!isRecording && trainingUrls.length + uploadingFiles.length >= MAX_NUMBER_OF_TRAINING_SAMPLES)

    return (
      <div className="TrainingSamples__recording">
        <Button
          styleType={isRecording ? 'danger' : 'gray'}
          size="large"
          icon={<Mic/>}
          caption={isRecording ? 'Stop recording' : 'Click to record audio'}
          type="button"
          onClick={handleRecordClick}
          disabled={isButtonDisabled}
        />
        {props.onOpenTextDescriptionMode && (
          <Button
            styleType="gray"
            size="large"
            icon={<EditSpeaker />}
            caption="Create voice from text description"
            type="button"
            onClick={handleOpenTextDescriptionModeClick}
          />
        )}
        {isPreparingRecording && (
          <span className="TrainingSamples__recording-status">
            Preparing microphone…
          </span>
        )}
        {isRecording && !isPreparingRecording && (
          <span className="TrainingSamples__recording-status">
            Recording…
          </span>
        )}
        {recordingError && (
          <span className="TrainingSamples__recording-error">{recordingError}</span>
        )}
      </div>
    )
  }

  const shouldRenderFiles = uploadingFilesComponents.length > 0 || existingFilesComponents.length > 0

  const renderFiles = () => {
    return (
      <div className="TrainingSamples__files">
        {uploadingFilesComponents}
        {existingFilesComponents}
      </div>
    )
  }

  return (
    <div className="TrainingSamples">
      <UploadArea
        showAlert={showLimitsUploadAlert}
        bottomComponent={renderRecordButton()}
        handleFileDrop={handleFileDrop}
        openFileManager={openFileManager}
      />
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        multiple
        hidden
        onChange={handleUploadClick}
      />
      {showLimitsUploadAlert && (
        <div className="TrainingSamples__limits-alert">
          {limitsUploadAlert}
        </div>
      )}
      {shouldRenderFiles && renderFiles()}
    </div>
  )
}

export default TrainingSamples
